import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import "../styles/AdminDashboard.css";

const API_BASE_URL = "https://primerise.space";
const HIDDEN_ACCOUNTS_API = `${API_BASE_URL}/api/admin/hidden-accounts`;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [hiddenAccounts, setHiddenAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);

  const [saving, setSaving] = useState(false);
  const [destructiveLoading, setDestructiveLoading] = useState(false);

  const getToken = () => {
    return (
      localStorage.getItem("token") ||
      localStorage.getItem("primeriseToken") ||
      localStorage.getItem("naxoraToken") ||
      ""
    );
  };

  const getHeaders = (includeJson = false) => {
    const token = getToken();
    const headers = {};

    if (includeJson) {
      headers["Content-Type"] = "application/json";
    }

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    return headers;
  };

  const handleUnauthorized = async () => {
    await Swal.fire({
      icon: "error",
      title: "Unauthorized",
      text: "Admin access required. Please login again.",
      confirmButtonColor: "#d4af37",
      background: "#0b0b0b",
      color: "#ffffff",
    });

    localStorage.removeItem("token");
    localStorage.removeItem("primeriseToken");
    localStorage.removeItem("primeriseAuth");
    localStorage.removeItem("primeriseCurrentUser");
    localStorage.removeItem("naxoraToken");
    localStorage.removeItem("naxoraAuth");
    localStorage.removeItem("isAuthenticated");
    localStorage.removeItem("naxoraCurrentUser");
    localStorage.removeItem("currentUser");

    window.location.href = "/login";
  };

  const getHiddenKey = (value) => {
    return String(value || "").trim().toLowerCase();
  };

  const isHiddenAccount = (user = {}) => {
    const userId = getHiddenKey(user.id);
    const userEmail = getHiddenKey(user.email);

    return hiddenAccounts.some((account) => {
      const hiddenKey = getHiddenKey(account.key || account.id || account.email);
      const hiddenEmail = getHiddenKey(account.email);

      return (
        (userId && hiddenKey && userId === hiddenKey) ||
        (userEmail && hiddenEmail && userEmail === hiddenEmail) ||
        (userEmail && hiddenKey && userEmail === hiddenKey)
      );
    });
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);

      const response = await fetch(`${API_BASE_URL}/api/admin/users`, {
        method: "GET",
        headers: getHeaders(),
      });

      const data = await response.json();

      if (response.status === 401 || response.status === 403) {
        await handleUnauthorized();
        return;
      }

      if (!response.ok) {
        throw new Error(data.message || "Failed to fetch users");
      }

      const hiddenResponse = await fetch(HIDDEN_ACCOUNTS_API, {
        method: "GET",
        headers: getHeaders(),
      });

      const hiddenData = await hiddenResponse.json();

      if (hiddenResponse.status === 401 || hiddenResponse.status === 403) {
        await handleUnauthorized();
        return;
      }

      if (!hiddenResponse.ok) {
        throw new Error(hiddenData.message || "Failed to fetch hidden accounts");
      }

      setUsers(Array.isArray(data.users) ? data.users : []);
      setHiddenAccounts(
        Array.isArray(hiddenData.hiddenAccounts) ? hiddenData.hiddenAccounts : []
      );
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Failed",
        text: error.message || "Unable to load users",
        confirmButtonColor: "#d4af37",
        background: "#0b0b0b",
        color: "#ffffff",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const visibleUsers = useMemo(() => {
    return users.filter((user) => !isHiddenAccount(user));
  }, [users, hiddenAccounts]);

  const filteredUsers = useMemo(() => {
    const keyword = search.trim().toLowerCase();

    if (!keyword) return visibleUsers;

    return visibleUsers.filter((user) => {
      return (
        String(user.name || "").toLowerCase().includes(keyword) ||
        String(user.email || "").toLowerCase().includes(keyword) ||
        String(user.referralCode || "").toLowerCase().includes(keyword)
      );
    });
  }, [visibleUsers, search]);

  const openEditModal = (user) => {
    setSelectedUser({
      ...user,
      balance: String(user.balance ?? 0),
      lockedBalance: String(user.lockedBalance ?? 0),
      totalProfit: String(user.totalProfit ?? 0),
    });
  };

  const closeEditModal = () => {
    setSelectedUser(null);
  };

  const handleSelectedChange = (field, value) => {
    setSelectedUser((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const getUserConfirmationValue = (user) => {
    return String(user.email || user.id || "").trim();
  };

  const runDestructiveAction = async ({
    user,
    title,
    text,
    endpoint,
    method,
    successTitle,
  }) => {
    if (!user || !user.id) return;

    const confirmationValue = getUserConfirmationValue(user);

    const result = await Swal.fire({
      icon: "warning",
      title,
      html: `
        <div style="text-align:left">
          <p>${text}</p>
          <p style="margin-top:10px;color:#d4af37">Type this to confirm:</p>
          <strong>${confirmationValue}</strong>
        </div>
      `,
      input: "text",
      inputPlaceholder: "Type user email or user ID",
      showCancelButton: true,
      confirmButtonText: "Confirm",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#d4af37",
      cancelButtonColor: "#333333",
      background: "#0b0b0b",
      color: "#ffffff",
      preConfirm: (value) => {
        if (
          String(value || "").trim().toLowerCase() !==
          confirmationValue.toLowerCase()
        ) {
          Swal.showValidationMessage("Confirmation does not match");
          return false;
        }

        return value;
      },
    });

    if (!result.isConfirmed) return;

    try {
      setDestructiveLoading(true);

      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method,
        headers: getHeaders(true),
        body: JSON.stringify({
          confirmation: confirmationValue,
        }),
      });

      const data = await response.json();

      if (response.status === 401 || response.status === 403) {
        await handleUnauthorized();
        return;
      }

      if (!response.ok) {
        throw new Error(data.message || "Action failed");
      }

      await Swal.fire({
        icon: "success",
        title: successTitle,
        text: data.message || "Action completed successfully",
        confirmButtonColor: "#d4af37",
        background: "#0b0b0b",
        color: "#ffffff",
      });

      await fetchUsers();
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Failed",
        text: error.message || "Unable to complete action",
        confirmButtonColor: "#d4af37",
        background: "#0b0b0b",
        color: "#ffffff",
      });
    } finally {
      setDestructiveLoading(false);
    }
  };

  const deleteUserDeposits = (user) => {
    runDestructiveAction({
      user,
      title: "Delete Deposit History?",
      text: "This will delete this user's deposit history only. User account and balance will not be deleted.",
      endpoint: `/api/admin-user-data/users/${user.id}/deposits`,
      method: "DELETE",
      successTitle: "Deposit History Deleted",
    });
  };

  const deleteUserWithdrawals = (user) => {
    runDestructiveAction({
      user,
      title: "Delete Withdrawal History?",
      text: "This will delete this user's withdrawal history only. User account and balance will not be deleted.",
      endpoint: `/api/admin-user-data/users/${user.id}/withdrawals`,
      method: "DELETE",
      successTitle: "Withdrawal History Deleted",
    });
  };

  const deleteUserSupportTickets = (user) => {
    runDestructiveAction({
      user,
      title: "Delete Support Tickets?",
      text: "This will delete this user's support tickets only. User account and balance will not be deleted.",
      endpoint: `/api/admin-user-data/users/${user.id}/support-tickets`,
      method: "DELETE",
      successTitle: "Support Tickets Deleted",
    });
  };

  const resetUserAccount = (user) => {
    runDestructiveAction({
      user,
      title: "Reset User Account?",
      text: "This will reset balance, locked balance, total profit, and KYC status. User login account will remain active.",
      endpoint: `/api/admin-user-data/users/${user.id}/reset`,
      method: "PATCH",
      successTitle: "User Account Reset",
    });
  };

  const fullDeleteUserAccount = (user) => {
    runDestructiveAction({
      user,
      title: "Full Delete User Account?",
      text: "This will delete this user from users, deposits, withdrawals, trades, KYC, support tickets, permissions, and related records. This action is dangerous.",
      endpoint: `/api/admin-user-data/users/${user.id}/full-delete`,
      method: "DELETE",
      successTitle: "User Fully Deleted",
    });
  };

  const saveBalanceChanges = async () => {
    if (!selectedUser) return;

    try {
      setSaving(true);

      const response = await fetch(
        `${API_BASE_URL}/api/admin/users/${selectedUser.id}/balance`,
        {
          method: "PUT",
          headers: getHeaders(true),
          body: JSON.stringify({
            balance: Number(selectedUser.balance || 0),
            lockedBalance: Number(selectedUser.lockedBalance || 0),
            totalProfit: Number(selectedUser.totalProfit || 0),
          }),
        }
      );

      const data = await response.json();

      if (response.status === 401 || response.status === 403) {
        await handleUnauthorized();
        return;
      }

      if (!response.ok) {
        throw new Error(data.message || "Failed to update balance");
      }

      Swal.fire({
        icon: "success",
        title: "Updated",
        text: data.message || "User balance updated successfully",
        confirmButtonColor: "#d4af37",
        background: "#0b0b0b",
        color: "#ffffff",
      });

      await fetchUsers();
      closeEditModal();
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Failed",
        text: error.message || "Unable to update balance",
        confirmButtonColor: "#d4af37",
        background: "#0b0b0b",
        color: "#ffffff",
      });
    } finally {
      setSaving(false);
    }
  };

  const updateUserStatus = async (userId, isActive) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/admin/users/${userId}/status`,
        {
          method: "PATCH",
          headers: getHeaders(true),
          body: JSON.stringify({ isActive }),
        }
      );

      const data = await response.json();

      if (response.status === 401 || response.status === 403) {
        await handleUnauthorized();
        return;
      }

      if (!response.ok) {
        throw new Error(data.message || "Failed to update status");
      }

      await fetchUsers();

      Swal.fire({
        icon: "success",
        title: "Updated",
        text: data.message || "User status updated successfully",
        confirmButtonColor: "#d4af37",
        background: "#0b0b0b",
        color: "#ffffff",
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Failed",
        text: error.message || "Unable to update status",
        confirmButtonColor: "#d4af37",
        background: "#0b0b0b",
        color: "#ffffff",
      });
    }
  };

  const updateUserRole = async (userId, isAdmin) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/admin/users/${userId}/role`,
        {
          method: "PATCH",
          headers: getHeaders(true),
          body: JSON.stringify({ isAdmin }),
        }
      );

      const data = await response.json();

      if (response.status === 401 || response.status === 403) {
        await handleUnauthorized();
        return;
      }

      if (!response.ok) {
        throw new Error(data.message || "Failed to update role");
      }

      await fetchUsers();

      Swal.fire({
        icon: "success",
        title: "Updated",
        text: data.message || "User role updated successfully",
        confirmButtonColor: "#d4af37",
        background: "#0b0b0b",
        color: "#ffffff",
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Failed",
        text: error.message || "Unable to update role",
        confirmButtonColor: "#d4af37",
        background: "#0b0b0b",
        color: "#ffffff",
      });
    }
  };

  const updateUserKyc = async (userId, kycStatus) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/admin/users/${userId}/kyc`,
        {
          method: "PATCH",
          headers: getHeaders(true),
          body: JSON.stringify({ kycStatus }),
        }
      );

      const data = await response.json();

      if (response.status === 401 || response.status === 403) {
        await handleUnauthorized();
        return;
      }

      if (!response.ok) {
        throw new Error(data.message || "Failed to update KYC status");
      }

      await fetchUsers();

      Swal.fire({
        icon: "success",
        title: "Updated",
        text: data.message || "User KYC updated successfully",
        confirmButtonColor: "#d4af37",
        background: "#0b0b0b",
        color: "#ffffff",
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Failed",
        text: error.message || "Unable to update KYC status",
        confirmButtonColor: "#d4af37",
        background: "#0b0b0b",
        color: "#ffffff",
      });
    }
  };

  return (
    <div className="admin-dashboard-page">
      <div className="admin-dashboard-wrapper">
        <div className="admin-topbar">
          <div>
            <h1>Admin Dashboard</h1>
            <p>User control and management panel</p>
          </div>

          <div className="admin-topbar-actions">
            <button
              className="admin-refresh-btn"
              onClick={() => navigate("/admin/community")}
            >
              Community Control
            </button>

            <button
              className="admin-refresh-btn"
              onClick={() => navigate("/admin/hidden-accounts")}
            >
              Hidden Accounts
            </button>

            <button className="admin-refresh-btn" onClick={fetchUsers}>
              Refresh
            </button>
          </div>
        </div>

        <div className="admin-stats-grid">
          <div className="admin-stat-card">
            <span>Total Users</span>
            <strong>{visibleUsers.length}</strong>
          </div>

          <div className="admin-stat-card">
            <span>Active Users</span>
            <strong>{visibleUsers.filter((user) => user.isActive).length}</strong>
          </div>

          <div className="admin-stat-card">
            <span>Admins</span>
            <strong>{visibleUsers.filter((user) => user.isAdmin).length}</strong>
          </div>

          <div className="admin-stat-card">
            <span>Verified KYC</span>
            <strong>
              {visibleUsers.filter((user) => user.kycStatus === "verified").length}
            </strong>
          </div>
        </div>

        <div className="admin-users-card">
          <div className="admin-card-top">
            <h2>Users</h2>

            <input
              type="text"
              placeholder="Search by name, email, referral code"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="admin-search-input"
            />
          </div>

          {loading ? (
            <div className="admin-empty-state">Loading users...</div>
          ) : filteredUsers.length === 0 ? (
            <div className="admin-empty-state">No users found</div>
          ) : (
            <div className="admin-users-table-wrap">
              <table className="admin-users-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Balance</th>
                    <th>Locked</th>
                    <th>Profit</th>
                    <th>KYC</th>
                    <th>Status</th>
                    <th>Role</th>
                    <th>Action</th>
                    <th>Data Control</th>
                  </tr>
                </thead>

                <tbody>
                  {filteredUsers.map((user) => (
                    <tr key={user.id}>
                      <td>{user.name || "-"}</td>
                      <td>{user.email || "-"}</td>
                      <td>${Number(user.balance || 0).toFixed(2)}</td>
                      <td>${Number(user.lockedBalance || 0).toFixed(2)}</td>
                      <td>${Number(user.totalProfit || 0).toFixed(2)}</td>

                      <td>
                        <select
                          value={user.kycStatus || "unverified"}
                          onChange={(e) => updateUserKyc(user.id, e.target.value)}
                          className="admin-table-select"
                        >
                          <option value="unverified">unverified</option>
                          <option value="pending">pending</option>
                          <option value="verified">verified</option>
                          <option value="rejected">rejected</option>
                        </select>
                      </td>

                      <td>
                        <button
                          className={`admin-badge-btn ${
                            user.isActive ? "active" : "inactive"
                          }`}
                          onClick={() => updateUserStatus(user.id, !user.isActive)}
                        >
                          {user.isActive ? "Active" : "Inactive"}
                        </button>
                      </td>

                      <td>
                        <button
                          className={`admin-badge-btn ${
                            user.isAdmin ? "admin" : "user"
                          }`}
                          onClick={() => updateUserRole(user.id, !user.isAdmin)}
                        >
                          {user.isAdmin ? "Admin" : "User"}
                        </button>
                      </td>

                      <td>
                        <button
                          className="admin-edit-btn"
                          onClick={() => openEditModal(user)}
                        >
                          Edit
                        </button>
                      </td>

                      <td>
                        <div className="admin-data-control-actions">
                          <button
                            type="button"
                            className="admin-data-btn"
                            disabled={destructiveLoading}
                            onClick={() => deleteUserDeposits(user)}
                          >
                            Delete Deposits
                          </button>

                          <button
                            type="button"
                            className="admin-data-btn"
                            disabled={destructiveLoading}
                            onClick={() => deleteUserWithdrawals(user)}
                          >
                            Delete Withdrawals
                          </button>

                          <button
                            type="button"
                            className="admin-data-btn"
                            disabled={destructiveLoading}
                            onClick={() => deleteUserSupportTickets(user)}
                          >
                            Delete Tickets
                          </button>

                          <button
                            type="button"
                            className="admin-data-btn warning"
                            disabled={destructiveLoading}
                            onClick={() => resetUserAccount(user)}
                          >
                            Reset
                          </button>

                          <button
                            type="button"
                            className="admin-data-btn danger"
                            disabled={destructiveLoading}
                            onClick={() => fullDeleteUserAccount(user)}
                          >
                            Full Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {selectedUser && (
          <div className="admin-modal-overlay">
            <div className="admin-modal">
              <div className="admin-modal-top">
                <h3>Edit User Balance</h3>
                <button className="admin-close-btn" onClick={closeEditModal}>
                  ×
                </button>
              </div>

              <div className="admin-modal-user">
                <span>{selectedUser.name}</span>
                <strong>{selectedUser.email}</strong>
              </div>

              <div className="admin-form-grid">
                <div className="admin-form-group">
                  <label>Balance</label>
                  <input
                    type="number"
                    value={selectedUser.balance}
                    onChange={(e) =>
                      handleSelectedChange("balance", e.target.value)
                    }
                  />
                </div>

                <div className="admin-form-group">
                  <label>Locked Balance</label>
                  <input
                    type="number"
                    value={selectedUser.lockedBalance}
                    onChange={(e) =>
                      handleSelectedChange("lockedBalance", e.target.value)
                    }
                  />
                </div>

                <div className="admin-form-group">
                  <label>Total Profit</label>
                  <input
                    type="number"
                    value={selectedUser.totalProfit}
                    onChange={(e) =>
                      handleSelectedChange("totalProfit", e.target.value)
                    }
                  />
                </div>
              </div>

              <div className="admin-modal-actions">
                <button className="admin-cancel-btn" onClick={closeEditModal}>
                  Cancel
                </button>

                <button
                  className="admin-save-btn"
                  onClick={saveBalanceChanges}
                  disabled={saving}
                >
                  {saving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}